import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardHeader from '@/components/Header/DashboardHeader';
import { Footer } from '@/components/organisms/Footer';
import { apiService } from '@/services/api';
import type { MarketCategory, MarketKind, MarketListingSummary } from '@/types/market';
import { ListingCard, useSavedIds } from './MarketShared';
import styles from './Market.module.scss';

export default function MarketBrowse() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { savedIds, toggleSave } = useSavedIds();

  const kind = (searchParams.get('kind') as MarketKind | null) || '';
  const category = searchParams.get('category') || '';
  const city = searchParams.get('city') || '';
  const qParam = searchParams.get('q') || '';

  const [q, setQ] = useState(qParam);
  const [categories, setCategories] = useState<MarketCategory[]>([]);
  const [listings, setListings] = useState<MarketListingSummary[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);

  useEffect(() => { setQ(qParam); }, [qParam]);

  // Category options depend on the selected kind (all when unset).
  useEffect(() => {
    mounted.current = true;
    (async () => {
      try {
        const cats = kind
          ? await apiService.mktGetCategories(kind)
          : [
              ...(await apiService.mktGetCategories('for_sale')),
              ...(await apiService.mktGetCategories('service')),
            ];
        if (mounted.current) setCategories(cats);
      } catch { /* non-fatal */ }
    })();
    return () => { mounted.current = false; };
  }, [kind]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.mktGetListings({
        kind: kind || undefined,
        category: category || undefined,
        q: qParam || undefined,
        city: city || undefined,
      });
      if (!mounted.current) return;
      setListings(res.listings);
      setNextCursor(res.nextCursor ?? null);
    } catch (e: any) {
      if (mounted.current) setError(e?.message || 'Failed to load listings.');
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [kind, category, qParam, city]);

  useEffect(() => { load(); }, [load]);

  const loadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await apiService.mktGetListings({
        kind: kind || undefined,
        category: category || undefined,
        q: qParam || undefined,
        city: city || undefined,
        cursor: nextCursor,
      });
      if (!mounted.current) return;
      setListings((prev) => {
        const seen = new Set(prev.map((l) => l.id));
        return [...prev, ...res.listings.filter((l) => !seen.has(l.id))];
      });
      setNextCursor(res.nextCursor ?? null);
    } catch (e: any) {
      if (mounted.current) setError(e?.message || 'Failed to load more.');
    } finally {
      if (mounted.current) setLoadingMore(false);
    }
  };

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    if (key === 'kind') next.delete('category'); // category is kind-scoped
    setSearchParams(next);
  };

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    updateParam('q', q.trim());
  };

  return (
    <>
      <DashboardHeader />
      <div className={styles.container}>
        <div className={styles.topBar}>
          <button type="button" className={styles.backButton} onClick={() => navigate('/customer/market')}>← Marketplace</button>
          <span className={styles.spacer} />
          <button type="button" className={styles.secondaryBtn} onClick={() => navigate('/customer/market/saved')}>♥ Saved</button>
          <button type="button" className={styles.primaryBtn} onClick={() => navigate('/customer/market/post')}>+ Post an ad</button>
        </div>
        <h1 className={styles.title}>Browse</h1>

        <form className={styles.searchRow} onSubmit={submitSearch}>
          <input className={styles.searchInput} placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} />
          <button type="submit" className={styles.secondaryBtn}>Search</button>
        </form>

        <div className={styles.filters}>
          <select className={styles.select} value={kind} onChange={(e) => updateParam('kind', e.target.value)}>
            <option value="">All types</option>
            <option value="for_sale">For sale</option>
            <option value="service">Services</option>
          </select>
          <select className={styles.select} value={category} onChange={(e) => updateParam('category', e.target.value)}>
            <option value="">All categories</option>
            {categories.map((c) => (
              <option key={c.code} value={c.code}>{c.name}</option>
            ))}
          </select>
          <input
            className={styles.select}
            placeholder="City"
            defaultValue={city}
            onBlur={(e) => updateParam('city', e.target.value.trim())}
            onKeyDown={(e) => { if (e.key === 'Enter') updateParam('city', (e.target as HTMLInputElement).value.trim()); }}
          />
        </div>

        {error && <p className={styles.error}>{error}</p>}
        {loading && <p className={styles.muted}><span className={styles.spinner} /> Loading…</p>}

        {!loading && listings.length === 0 && !error && (
          <div className={styles.empty}>
            <div className={styles.emptyIcon}>🔍</div>
            No listings match your search.
          </div>
        )}

        {!loading && listings.length > 0 && (
          <>
            <div className={styles.grid}>
              {listings.map((l) => (
                <ListingCard key={l.id} listing={l} onClick={() => navigate(`/customer/market/listing/${l.id}`)}
                  saved={savedIds.has(l.id)} onToggleSave={() => toggleSave(l.id)} />
              ))}
            </div>
            {nextCursor && (
              <div className={styles.loadMoreWrap}>
                <button type="button" className={styles.secondaryBtn} onClick={loadMore} disabled={loadingMore}>
                  {loadingMore ? 'Loading…' : 'Load more'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
      <Footer />
    </>
  );
}
